import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper";
import ButtonEdgeRounded from "§/src/components/atoms/buttons/button-edge-rounded";
import classNames from "classnames"

const FullScreenGalleryModal = ({ isOpen, onClose, images, initialIndex }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const sliderId = useMemo(() => "gallery-modal-" + Math.floor(Math.random() * 9999), [])
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [swiperRef, setSwiperRef] = useState(null);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const zoomLevelRef = useRef(1);

  const resetAllZoom = useCallback(() => {
    zoomLevelRef.current = 1;
    setZoomLevel(1);
    setZoomPosition({ x: 0, y: 0 });
    if (modalRef.current) {
      const allWrappers = modalRef.current.querySelectorAll('.image-wrapper');
      allWrappers.forEach(wrapper => {
        wrapper.style.transform = 'scale(1) translate(0%, 0%)';
      });
    }
  }, []);

  const handleNavigation = useCallback((direction) => {
    if (swiperRef) {
      if (direction === 'prev') {
        swiperRef.slidePrev();
      } else {
        swiperRef.slideNext();
      }
      resetAllZoom();
    }
  }, [swiperRef, resetAllZoom]);

  useEffect(() => {
    if (isOpen && swiperRef) {
      swiperRef.slideTo(initialIndex, 0);
      setActiveIndex(initialIndex);
      resetAllZoom();
    }
  }, [isOpen, initialIndex, swiperRef, resetAllZoom]);

  useEffect(() => {
    if (isOpen && swiperRef) {
      swiperRef.slideTo(initialIndex, 0);
      setActiveIndex(initialIndex);
    }
  }, [isOpen, initialIndex, swiperRef]);

  // Disabilita/abilita lo swiper in base al livello di zoom
  useEffect(() => {
    if (swiperRef) {
      if (zoomLevel > 1) {
        swiperRef.allowTouchMove = false;
        swiperRef.allowSlideNext = false;
        swiperRef.allowSlidePrev = false;
      } else {
        swiperRef.allowTouchMove = true;
        swiperRef.allowSlideNext = true;
        swiperRef.allowSlidePrev = true;
      }
    }
  }, [zoomLevel, swiperRef]);

  const updateTransform = useCallback(() => {
    if (modalRef.current) {
      const wrapper = modalRef.current.querySelector('.swiper-slide-active .image-wrapper');
      if (wrapper) {
        wrapper.style.transform = `scale(${zoomLevelRef.current}) translate(${zoomPosition.x * 100}%, ${zoomPosition.y * 100}%)`;
      }
    }
  }, [zoomPosition]);

  const handleZoomIn = () => {
    zoomLevelRef.current = Math.min(zoomLevelRef.current + 0.5, 3);
    setZoomLevel(zoomLevelRef.current);
    updateTransform();
  };

  const handleZoomOut = () => {
    zoomLevelRef.current = Math.max(zoomLevelRef.current - 0.5, 1);
    setZoomLevel(zoomLevelRef.current);
    if (zoomLevelRef.current <= 1) {
      setZoomPosition({ x: 0, y: 0 });
    }
    updateTransform();
  };

  const handleMouseDown = (e) => {
    if (zoomLevelRef.current <= 1) return;

    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setLastPosition({ ...zoomPosition });

    // Prevent image dragging
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || zoomLevelRef.current <= 1) return;

    e.preventDefault(); // Previene il comportamento di default del drag

    const dx = (e.clientX - dragStart.x) / (modalRef.current?.offsetWidth || 1);
    const dy = (e.clientY - dragStart.y) / (modalRef.current?.offsetHeight || 1);

    // Aumenta il range di movimento in base al livello di zoom
    const rangeX = (zoomLevelRef.current - 1) / (1.5 * zoomLevelRef.current);
    const rangeY = (zoomLevelRef.current - 1) / (1.5 * zoomLevelRef.current);

    const newX = Math.max(-rangeX, Math.min(rangeX, lastPosition.x + dx));
    const newY = Math.max(-rangeY, Math.min(rangeY, lastPosition.y + dy));

    setZoomPosition({ x: newX, y: newY });
    updateTransform();
  }, [isDragging, dragStart, lastPosition, updateTransform]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle mouse leaving the window
  useEffect(() => {
    const handleMouseLeave = () => {
      setIsDragging(false);
    };

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="full-screen-gallery-modal"
      ref={modalRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{
        cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
      }}
    >
      <button onClick={onClose} type="button" className="close-button btn-close m-0 p-0" />
      <Swiper
        onSwiper={setSwiperRef}
        onSlideChange={swiper => {
          setActiveIndex(swiper.activeIndex);
          resetAllZoom();
        }}
        autoplay={false}
        slidesPerView={1}
        spaceBetween={0}
        modules={[Navigation, Pagination]}
        className="simple-swiper"
        id={sliderId}
      >
        {images.map((image, index) => (
          <SwiperSlide key={index}>
            <div
              className="image-wrapper"
              style={{
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }}
            >
              <GatsbyImage
                image={getImage(image.gatsbyImageFull)}
                alt={image.altText || ''}
                objectFit="contain"
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {images.length > 1 && (
        <div className="sw-navigation">
          <ButtonEdgeRounded
            id={sliderId + "-nav-prev"}
            dir="start"
            onClick={() => handleNavigation('prev')}
          ></ButtonEdgeRounded>
          <ButtonEdgeRounded
            id={sliderId + "-nav-next"}
            dir="end"
            onClick={() => handleNavigation('next')}
          ></ButtonEdgeRounded>
        </div>
      )}

      <div className="modal-controls">
        <div className="zoom-controls">
          <button onClick={handleZoomOut}>
            <svg width="16" height="2" viewBox="0 0 16 2" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="16" y1="1" x2="-8.74228e-08" y2="0.999999" stroke="#1D1D1D" strokeWidth="2"/>
            </svg>
          </button>
          <button onClick={handleZoomIn}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="8" y1="4.37114e-08" x2="8" y2="16" stroke="#1D1D1D" strokeWidth="2"/>
              <line x1="16" y1="8" x2="-8.74228e-08" y2="8" stroke="#1D1D1D" strokeWidth="2"/>
            </svg>
          </button>
        </div>

        {images.length > 1 && (
          <div className="swiper-main-pagination position-relative pt-3">
            <div
              id={`${sliderId}-pagination`}
              className="swiper-pagination swiper-pagination-clickable swiper-pagination-bullets swiper-pagination-horizontal"
            >
              {images.map((elem, i) => (
                <span
                  key={i}
                  className={classNames("swiper-pagination-bullet", {
                    "spinner-border": i === activeIndex,
                    "border-dark": i === activeIndex,
                  })}
                  onClick={() => {
                    swiperRef.slideTo(i);
                    resetAllZoom();
                  }}
                ></span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FullScreenGalleryModal;
